import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick'

export interface ImportedCSVContact {
  name?: string
  emails?: string[]
  phones?: string[]
  organization?: string
  role?: string
  relationship?: string
  status?: string
  subtype?: string
  location?: string
  birthday?: string
  tags?: string[]
  summary?: string
  notes?: string[]
  relationship_type?: string
  key_context?: string
  action_items?: string
  /** User-visible kanban column label or stage slug (e.g. "Family",
   *  "Inner Circle", "close"). The caller resolves to the active
   *  module's stage at import time. */
  kanbanColumn?: string
  /** Round-trip support: AI-generated bio paragraph. */
  enrichmentSummary?: string
  /** Round-trip support: AI-generated skills, e.g. ["Data Visualization", ...]. */
  enrichmentSkills?: string[]
  /** Round-trip support: contact title (separate from role). */
  contactTitle?: string
  /** Round-trip support: LinkedIn profile URL. */
  linkedinUrl?: string
}

// Maps common CSV header names to our contact fields
const HEADER_MAP: Record<string, string> = {
  name: 'name',
  full_name: 'name',
  display_name: 'name',
  'display name': 'name',
  first_name: 'first_name',
  'first name': 'first_name',
  firstname: 'first_name',
  given_name: 'first_name',
  'given name': 'first_name',
  last_name: 'last_name',
  'last name': 'last_name',
  lastname: 'last_name',
  family_name: 'last_name',
  'family name': 'last_name',
  surname: 'last_name',
  email: 'email',
  email_address: 'email',
  'email address': 'email',
  'e-mail': 'email',
  'e-mail address': 'email',
  'e-mail 1 - value': 'email',
  phone: 'phone',
  phone_number: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
  tel: 'phone',
  telephone: 'phone',
  'phone 1 - value': 'phone',
  company: 'organization',
  organization: 'organization',
  org: 'organization',
  'organization 1 - name': 'organization',
  role: 'role',
  title: 'role',
  job_title: 'role',
  'job title': 'role',
  'organization 1 - title': 'role',
  relationship: 'relationship',
  location: 'location',
  address: 'location',
  city: 'location',
  birthday: 'birthday',
  dob: 'birthday',
  date_of_birth: 'birthday',
  'date of birth': 'birthday',
  tags: 'tags',
  summary: 'summary',
  notes: 'notes',
  note: 'notes',
  status: 'status',
  subtype: 'subtype',
  relationship_type: 'relationship_type',
  key_context: 'key_context',
  action_items: 'action_items',
  kanban_column: 'kanban_column',
  'kanban column': 'kanban_column',
  kanban: 'kanban_column',
  column: 'kanban_column',
  classification: 'kanban_column',
  stage: 'kanban_column',
  pipeline_stage: 'kanban_column',
  'pipeline stage': 'kanban_column',
  ai_summary: 'ai_summary',
  'ai summary': 'ai_summary',
  ai_bio: 'ai_summary',
  'ai bio': 'ai_summary',
  bio: 'ai_summary',
  ai_skills: 'ai_skills',
  'ai skills': 'ai_skills',
  skills: 'ai_skills',
  contact_title: 'contact_title',
  'contact title': 'contact_title',
  linkedin: 'linkedin',
  linkedin_url: 'linkedin',
  'linkedin url': 'linkedin',
  'linkedin profile': 'linkedin',
}

/**
 * RFC 4180-compliant CSV row splitter.
 * Handles quoted fields that contain commas, newlines, and escaped double-quotes.
 * Returns an array of rows, where each row is an array of field strings.
 */
function parseCSVRows(text: string): string[][] {
  const rows: string[][] = []
  let fields: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === ',') {
        fields.push(current.trim())
        current = ''
      } else if (ch === '\r') {
        // skip \r, handle newline on \n
        if (i + 1 < text.length && text[i + 1] === '\n') continue
        // bare \r as line ending
        fields.push(current.trim())
        if (fields.some(f => f)) rows.push(fields)
        fields = []
        current = ''
      } else if (ch === '\n') {
        fields.push(current.trim())
        if (fields.some(f => f)) rows.push(fields)
        fields = []
        current = ''
      } else {
        current += ch
      }
    }
  }
  // flush last row
  fields.push(current.trim())
  if (fields.some(f => f)) rows.push(fields)

  return rows
}

function parseCSV(text: string): { contacts: ImportedCSVContact[]; warnings: string[] } {
  const contacts: ImportedCSVContact[] = []
  const warnings: string[] = []

  // Strip BOM
  const clean = text.replace(/^\uFEFF/, '')
  const rows = parseCSVRows(clean)
  if (rows.length < 2) {
    warnings.push('CSV has no data rows')
    return { contacts, warnings }
  }

  const headers = rows[0]
  const mappedHeaders = headers.map(h => HEADER_MAP[h.toLowerCase().trim()] || null)

  // Check we have at least a name column
  const hasName = mappedHeaders.includes('name') || (mappedHeaders.includes('first_name') && mappedHeaders.includes('last_name'))
  if (!hasName) {
    // Try to find any usable name-like column
    const nameIdx = headers.findIndex(h => /name/i.test(h))
    if (nameIdx >= 0) mappedHeaders[nameIdx] = 'name'
  }

  for (let i = 1; i < rows.length; i++) {
    const fields = rows[i]

    const row: Record<string, string> = {}
    for (let j = 0; j < mappedHeaders.length; j++) {
      const key = mappedHeaders[j]
      if (key && fields[j]) {
        row[key] = fields[j]
      }
    }

    // Build name
    let name = row.name || ''
    if (!name && (row.first_name || row.last_name)) {
      name = `${row.first_name || ''} ${row.last_name || ''}`.trim()
    }

    // If still no name but we have an email, derive a display name from the
    // email local-part: "harris.hailey@alfaro-mail.com" → "Harris Hailey".
    // Fall back to the raw email string if the local-part can't be parsed.
    if (!name && row.email) {
      const firstEmail = row.email.split(/;\s*/)[0].trim()
      const localPart = firstEmail.split('@')[0]
      if (localPart) {
        const parts = localPart.split(/[._+\-]+/).filter(Boolean)
        if (parts.length > 0) {
          name = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ')
        } else {
          name = firstEmail
        }
      } else {
        name = firstEmail
      }
      warnings.push(`Row ${i + 1}: imported without name (derived from email)`)
    }

    // Only skip a row when it has no name AND no email AND no phone
    if (!name && !row.email && !row.phone) {
      warnings.push(`Row ${i + 1}: skipped — no name, email, or phone found`)
      continue
    }

    // If we still have no name (no email either, but have a phone), use a placeholder
    if (!name) {
      name = row.phone || 'Unknown'
      warnings.push(`Row ${i + 1}: imported without name`)
    }

    const contact: ImportedCSVContact = { name }
    // Multi-email/phone round-trip: exporter joins arrays with `; `; split
     // back on the same delimiter so contacts with multiple addresses don't
     // lose data through export → re-import. Single-value cells (no `;`)
     // produce a one-element array, so older CSVs still work unchanged.
    if (row.email) contact.emails = row.email.split(/;\s*/).filter(Boolean)
    if (row.phone) contact.phones = row.phone.split(/;\s*/).filter(Boolean)
    if (row.organization) contact.organization = row.organization
    if (row.role) contact.role = row.role
    if (row.relationship) contact.relationship = row.relationship
    if (row.status) contact.status = row.status
    if (row.subtype) contact.subtype = row.subtype
    if (row.location) contact.location = row.location
    if (row.birthday) contact.birthday = row.birthday
    if (row.tags) contact.tags = row.tags.split(/;\s*/).filter(Boolean)
    if (row.summary) contact.summary = row.summary
    if (row.notes) contact.notes = [row.notes]
    if (row.relationship_type) contact.relationship_type = row.relationship_type
    if (row.key_context) contact.key_context = row.key_context
    if (row.action_items) contact.action_items = row.action_items
    if (row.kanban_column) contact.kanbanColumn = row.kanban_column
    if (row.ai_summary) contact.enrichmentSummary = row.ai_summary
    if (row.ai_skills) contact.enrichmentSkills = row.ai_skills.split(/;\s*/).filter(Boolean)
    if (row.contact_title) contact.contactTitle = row.contact_title
    if (row.linkedin) contact.linkedinUrl = row.linkedin

    contacts.push(contact)
  }

  return { contacts, warnings }
}

interface ImportCSVModalProps {
  onImport: (contacts: ImportedCSVContact[]) => Promise<void>
  onClose: () => void
  /** Optional file to auto-parse on mount. Used by the chat composer's
   *  "Looks like a contact list — Import?" nudge so the user doesn't
   *  have to re-pick a file they just attached. */
  initialFile?: File | null
}

export function ImportCSVModal({ onImport, onClose, initialFile }: ImportCSVModalProps) {
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30)
  const [step, setStep] = useState<'upload' | 'preview'>('upload')
  const [contacts, setContacts] = useState<ImportedCSVContact[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleParse = (text: string) => {
    const result = parseCSV(text)
    setContacts(result.contacts)
    setWarnings(result.warnings)
    setStep('preview')
  }

  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      if (text) handleParse(text)
    }
    reader.readAsText(file)
  }

  // If the caller passed an initialFile (from the chat nudge), parse it
  // immediately on mount. Use a ref-checked one-shot so re-mounts caused
  // by parent re-renders don't reparse the same file. handleFile is
  // intentionally not in deps — it's a stable closure within this render
  // and adding it would cause an infinite loop on every render.
  const initialFileRef = useRef<File | null>(null)
  useEffect(() => {
    if (initialFile && initialFileRef.current !== initialFile) {
      initialFileRef.current = initialFile
      handleFile(initialFile)
    }
  }, [initialFile])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const handleConfirm = async () => {
    setIsImporting(true)
    try {
      await onImport(contacts)
    } finally {
      setIsImporting(false)
    }
  }

  const portalRoot = document.getElementById('modal-portal-root')
  if (!portalRoot) return null

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
      }}
      onClick={handleOverlayClick}
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        ref={modalContentRef}
        className="xv-scroll"
        style={{
          background: 'var(--color-surface, white)', borderRadius: 12,
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)', width: 480, maxHeight: '80vh',
          overflow: 'auto', fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)' }}>
            {step === 'upload' ? 'Import CSV' : `Preview — ${contacts.length} contacts`}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-text-muted)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {step === 'upload' && (
            <>
              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--color-border)', borderRadius: 10,
                  padding: '40px 20px', textAlign: 'center', cursor: 'pointer',
                  marginBottom: 16, transition: 'border-color 0.2s',
                }}
                onDragEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-primary)' }}
                onDragLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border)' }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" style={{ marginBottom: 8 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <div style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
                  Drop a CSV file here, or click to browse
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]) }}
                />
              </div>

              {/* Paste option */}
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>Or paste CSV content:</div>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="name,email,phone,organization&#10;John Doe,john@example.com,555-1234,Acme Inc"
                rows={5}
                style={{
                  width: '100%', padding: 10, border: '1px solid var(--color-border)',
                  borderRadius: 8, fontSize: 13, fontFamily: 'monospace', resize: 'vertical',
                  color: 'var(--color-text)', background: 'var(--color-surface)', boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => { if (pasteText.trim()) handleParse(pasteText) }}
                disabled={!pasteText.trim()}
                style={{
                  marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: pasteText.trim() ? 'var(--color-primary, #1a9ba5)' : '#ccc',
                  color: 'white', fontSize: 13, fontWeight: 500, cursor: pasteText.trim() ? 'pointer' : 'default',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Parse
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              {/* Warnings */}
              {warnings.length > 0 && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fef3c7', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                  {warnings.map((w, i) => <div key={i}>{w}</div>)}
                </div>
              )}

              {/* Preview table */}
              <div className="xv-scroll" style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--color-surface-hover, #f9fafb)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--color-text-muted)' }}>Name</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--color-text-muted)' }}>Email</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--color-text-muted)' }}>Phone</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--color-text-muted)' }}>Org</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.slice(0, 50).map((c, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text)' }}>{c.name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text-muted)' }}>{c.emails?.[0] || ''}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text-muted)' }}>{c.phones?.[0] || ''}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text-muted)' }}>{c.organization || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {contacts.length > 50 && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center' }}>
                    ...and {contacts.length - 50} more
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <button
                  onClick={() => { setStep('upload'); setContacts([]); setWarnings([]); setPasteText(''); }}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', fontSize: 13, cursor: 'pointer', color: 'var(--color-text)', fontFamily: "'DM Sans', sans-serif" }}
                >
                  Back
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={contacts.length === 0 || isImporting}
                  style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none',
                    background: contacts.length > 0 ? 'var(--color-primary, #1a9ba5)' : '#ccc',
                    color: 'white', fontSize: 13, fontWeight: 500,
                    cursor: contacts.length > 0 ? 'pointer' : 'default',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {isImporting ? 'Importing...' : `Import ${contacts.length} Contacts`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    portalRoot,
  )
}
